<script setup lang="ts">
import { ref, computed } from 'vue';

type Operation = {
  label: string;
  value: string;
};

type Option = {
  label: string;
  value: string;
};

type Field = {
  label: string;
  value: string;
  operations: Operation[];
  options: Option[];
};

type Condition = {
  field: string;
  operation: string;
  value: any;
};

type Rule = {
  type: string;
  params: {
    message: string;
  };
};

type ConditionGroup = {
  conditions: Condition[];
  rule: Rule;
};

const props = defineProps<{
  conditionFields: Field[],
  rules: ConditionGroup[],
  ruleTypes: { label: string, value: string }[]
}>();

const operations = (field: string) => {
  return props.conditionFields.find(f => f.value === field)?.operations || [];
}
const options = (field: string) => {
  return props.conditionFields.find(f => f.value === field)?.options || [];
}

const productTestRules = ref<ConditionGroup[]>( props.rules || [
  {
    conditions: [{ field: '', operation: '', value: null }],
    rule: { type: 'disallow', params: { message: '' } }
  }
]);

const addCondition = (groupIndex: number) => {
  productTestRules.value[groupIndex].conditions.push({ field: '', operation: '', value: null });
};

const removeCondition = (groupIndex: number, conditionIndex: number) => {
  productTestRules.value[groupIndex].conditions.splice(conditionIndex, 1);
};

const addConditionGroup = () => {
  productTestRules.value.push({
    conditions: [{ field: '', operation: '', value: null }],
    rule: { type: 'disallow', params: { message: '' } }
  });
};

const removeConditionGroup = (groupIndex: number) => {
  productTestRules.value.splice(groupIndex, 1);
};

const generateRuleJson = computed(() => {
  return productTestRules.value.map(group => ({
    conditions: group.conditions.map(c => ({
      field: c.field,
      operation: c.operation,
      value: c.value
    })),
    rule: group.rule
  }));
});
</script>

<template>
  <div>
    <div v-for="(group, groupIndex) in productTestRules" :key="groupIndex" class="card mb-4">
      <div class="card-header d-flex justify-content-between align-items-center">
        <span>
          {{ $t('Condition Group') }} {{ groupIndex + 1 }}
        </span>
        <a @click.prevent="removeConditionGroup(groupIndex)" class="text-danger">
          <i class="fa fa-trash-o"></i>
        </a>
      </div>
      <div class="card-body">
        <div>
          <strong class="d-block mb-2">{{ $t('Conditions') }}</strong>
          <div v-for="(condition, conditionIndex) in group.conditions" :key="conditionIndex" class="d-flex justify-content-start align-items-center">
            <div class="input-group mb-2 mr-sm-2">
              <select v-model="condition.field" class="form-control">
                <option disabled value="">{{ $t('Select Field') }}</option>
                <option v-for="field in props.conditionFields" :key="field.value" :value="field.value">{{ field.label }}</option>
              </select>
            </div>

            <div class="input-group mb-2 mr-sm-2">
              <select v-model="condition.operation" class="form-control">
                <option disabled value="">{{ $t('Select Operation') }}</option>
                <option v-for="operation in operations(condition.field)"
                        :key="operation.value"
                        :value="operation.value">{{ operation.label }}</option>
              </select>
            </div>

            <div class="input-group mb-2 mr-sm-2">
              <select v-model="condition.value" class="form-control">
                <option disabled value="">{{ $t('Select Value') }}</option>
                <option v-for="option in options(condition.field)"
                        :key="option.value"
                        :value="String(option.value)">{{ option.label }}</option>
              </select>
            </div>

            <div class="ml-auto mb-2">
              <button type="button" @click.prevent="removeCondition(groupIndex, conditionIndex)" class="btn btn-link text-danger">
                <i class="fa fa-times"></i>
              </button>
            </div>
          </div>

          <button type="button" @click.prevent="addCondition(groupIndex)" class="btn btn-light">{{ $t('Add Condition') }}</button>
        </div>

        <div class="mt-4">
          <strong class="d-block mb-2">{{ $t('Behavior') }}</strong>

          <div class="rule-section">
            <div class="form-group">
              <select v-model="group.rule.type" class="form-control">
                <option disabled value="">{{ $t('Select Rule Type') }}</option>
                <option v-for="ruleType in props.ruleTypes"
                        :key="ruleType.value"
                        :value="ruleType.value">{{ ruleType.label }}</option>
              </select>
            </div>
            <div class="form-group">
              <textarea v-model="group.rule.params.message" placeholder="Rule message" class="form-control" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <button type="button" @click.prevent="addConditionGroup" class="btn btn-light mb-4">{{ $t('Add Condition Group') }}</button>

    <input type="hidden" name="product_test_rules" :value="JSON.stringify(generateRuleJson)" />
  </div>
</template>